import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';


export interface MacroPulseData {
  status: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  risk_score: string;
  risk_appetite_state: 'RISK_ON' | 'RISK_OFF' | 'BALANCED';
  liquidity_state: 'EXPANSION' | 'CONTRACTION' | 'NEUTRAL';
  inflation_state: 'HOT' | 'COOLING' | 'STABLE';
  ftse_a50_sentiment: 'BULLISH' | 'BEARISH' | 'FLAT';
  flight_to_safety: boolean;
  anchor_count: number;
  as_of_utc: string;
  vector_name?: string;
  message?: string;
}

export interface MacroAnchorItem {
  symbol: string;
  category: string;
  display_name: string;
  provider_source: string;
  provider_ticker: string;
  unit: string;
  currency: string;
  timezone: string;
  precision: string;
  last_price: string;
  prev_close: string;
  change: string;
  change_percent: string;
  status: string;
  updated_at_utc: string;
}

export interface MacroAnchorsResponse {
  status: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  total: number;
  anchors: MacroAnchorItem[];
  vector_name?: string;
  as_of_utc: string;
}

const ANCHOR_ROLES: Record<string, string> = {
  IDX_FTSE_A50: 'A股早盘情绪先行标尺 (09:15-09:30 CST 关键指引)',
  IDX_NASDAQ100: '全球科技权益风险偏好 · 与比特币走势高度共振',
  IDX_SP500: '全球大类资产与机构风险敞口核心锚',
  FX_DXY: '全球流动性水龙头 · 强势拉升对风险资产构成逆风',
  BOND_US10Y: '全球无风险资产折现率基石 · 估值锚点',
  COMM_GOLD: '地缘避险警报与抗信用通胀硬通货',
  COMM_OIL_BRENT: '全球实体工业需求与短期 CPI 输入型通胀计',
  COMM_COPPER: '周期工业之王 · 全球经济复苏与制造业晴雨表',
};

export const GlobalMacroRegimeView: React.FC<{ colorMode?: 'mainland' | 'international' }> = ({
  colorMode = 'mainland',
}) => {
  const [pulse, setPulse] = useState<MacroPulseData | null>(null);
  const [anchors, setAnchors] = useState<MacroAnchorItem[]>([]);
  const [state, setState] = useState<'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty'>('loading');
  const [selectedVector, setSelectedVector] = useState<string>('LIVE');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchMacroData = useCallback(async (vectorKey?: string) => {
    setState('loading');
    setErrorMsg(null);
    try {
      const vQuery = (vectorKey && vectorKey !== 'LIVE') ? `?vector=${vectorKey}` : '';
      const [pulseRes, anchorsRes] = await Promise.all([
        apiClient.get<MacroPulseData>(`/api/v1/market/macro/pulse${vQuery}`),
        apiClient.get<MacroAnchorsResponse>(`/api/v1/market/macro/anchors${vQuery}`),
      ]);

      setPulse(pulseRes);
      setAnchors(anchorsRes.anchors || []);
      setState(pulseRes.status || 'fresh');
    } catch (err: any) {
      setState('disconnected');
      setErrorMsg(err?.message || '无法获取宏观行情与态势感知数据');
    }
  }, []);

  useEffect(() => {
    fetchMacroData(selectedVector);
  }, [fetchMacroData, selectedVector]);

  // Color mappings
  const isUpPositive = colorMode === 'international';
  const getChangeColor = (pctStr: string) => {
    const n = Number(pctStr);
    if (isNaN(n) || n === 0) return 'var(--text-muted)';
    if (n > 0) return isUpPositive ? 'var(--color-profit, #10b981)' : 'var(--color-loss, #ef4444)';
    return isUpPositive ? 'var(--color-loss, #ef4444)' : 'var(--color-profit, #10b981)';
  };

  return (
    <div className="macro-regime-view" style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* Header Banner */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div className="bilingual-view-header">
          <div className="bilingual-header-top">
            <h2 className="bilingual-header-title-zh" style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
              🌐 1. 全球宏观情势罗盘与参考锚点
            </h2>
            <span
              style={{
                fontSize: '0.75rem',
                padding: '2px 8px',
                borderRadius: '12px',
                background:
                  state === 'fresh'
                    ? 'rgba(16, 185, 129, 0.15)'
                    : state === 'stale'
                    ? 'rgba(245, 158, 11, 0.15)'
                    : 'rgba(239, 68, 68, 0.15)',
                color:
                  state === 'fresh'
                    ? '#10b981'
                    : state === 'stale'
                    ? '#f59e0b'
                    : '#ef4444',
                fontWeight: 600,
              }}
            >
              {state === 'fresh' && '● 状态正常 (FRESH)'}
              {state === 'stale' && '▲ 数据延迟 (STALE)'}
              {state === 'disconnected' && '✖ 离线中断 (DISCONNECTED)'}
              {state === 'loading' && '○ 正在计算 (LOADING)'}
              {state === 'empty' && '∅ 暂无数据 (EMPTY)'}
            </span>
          </div>
          <span className="bilingual-header-subtitle-en">GLOBAL MACRO REGIME COMPASS & ANCHORS</span>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Stage 029 宏观大宗参考锚点体系 · 仅作为股票/加密市场环境定量参考 · 100% 只读 · 零商业交易
          </p>
        </div>

        {/* Vector State Preset Selector (For Testing & Observation) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>行情数据源:</span>
          {[
            { key: 'LIVE', label: '● 实盘实时数据 (LIVE)' },
            { key: 'VECTOR_01_BALANCED', label: '模拟: 平稳再平衡 (V1)' },
            { key: 'VECTOR_02_RISK_ON_EXPANSION', label: '模拟: 风险偏好扩张 (V2)' },
            { key: 'VECTOR_03_RISK_OFF_SHOCK', label: '模拟: 避险冲击收缩 (V3)' },
          ].map((v) => (
            <button
              key={v.key}
              onClick={() => setSelectedVector(v.key)}
              style={{
                fontSize: '0.74rem',
                padding: '4px 10px',
                borderRadius: '16px',
                border: selectedVector === v.key ? '1px solid var(--accent-sage, #3b82f6)' : '1px solid var(--border-subtle)',
                background: selectedVector === v.key
                  ? (v.key === 'LIVE' ? 'rgba(16, 185, 129, 0.16)' : 'rgba(59, 130, 246, 0.14)')
                  : 'var(--bg-surface)',
                color: selectedVector === v.key
                  ? (v.key === 'LIVE' ? '#10b981' : 'var(--accent-sage, #3b82f6)')
                  : 'var(--text-muted)',
                cursor: 'pointer',
                fontWeight: selectedVector === v.key ? 600 : 400,
                transition: 'all 0.15s ease',
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
      </header>

      {/* Geopolitical Flight to Safety Warning */}
      {pulse?.flight_to_safety && (
        <div
          style={{
            padding: '10px 16px',
            marginBottom: '18px',
            borderRadius: '6px',
            background: 'rgba(239, 68, 68, 0.12)',
            border: '1px solid rgba(239, 68, 68, 0.35)',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          <span>🚨 地缘极度避险信号激活 (Flight-to-Safety)</span>
          <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--text-primary)' }}>
            COMEX 黄金单日暴涨超过 1.0% 且全球风险资产全面处于 RISK_OFF 状态，请重点监控加密货币及成长股杠杆踩踏风险！
          </span>
        </div>
      )}

      {/* Macro Regime Pulse Compass Ribbon */}
      <div
        className="macro-pulse-ribbon"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '14px',
          marginBottom: '24px',
        }}
      >
        {/* Risk Appetite Card */}
        <div
          style={{
            padding: '16px',
            borderRadius: '8px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            全球风险偏好得分 (R-Score)
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'monospace' }}>
              {pulse?.risk_score ?? '--'}
            </span>
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '4px',
                background:
                  pulse?.risk_appetite_state === 'RISK_ON'
                    ? 'rgba(16, 185, 129, 0.16)'
                    : pulse?.risk_appetite_state === 'RISK_OFF'
                    ? 'rgba(239, 68, 68, 0.16)'
                    : 'rgba(100, 116, 139, 0.16)',
                color:
                  pulse?.risk_appetite_state === 'RISK_ON'
                    ? '#10b981'
                    : pulse?.risk_appetite_state === 'RISK_OFF'
                    ? '#ef4444'
                    : 'var(--text-muted)',
              }}
            >
              {pulse?.risk_appetite_state === 'RISK_ON' && '🚀 RISK ON (偏好扩张)'}
              {pulse?.risk_appetite_state === 'RISK_OFF' && '🛡️ RISK OFF (避险收缩)'}
              {pulse?.risk_appetite_state === 'BALANCED' && '⚖️ BALANCED (相对平衡)'}
              {!pulse?.risk_appetite_state && '--'}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '6px' }}>
            纳指100 (60%) + 标普500 (40%) 复合动能
          </div>
        </div>

        {/* Global Liquidity Card */}
        <div
          style={{
            padding: '16px',
            borderRadius: '8px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            全球流动性环境 (Liquidity State)
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '2px' }}>
            <span
              style={{
                padding: '3px 10px',
                borderRadius: '4px',
                background:
                  pulse?.liquidity_state === 'EXPANSION'
                    ? 'rgba(6, 182, 212, 0.16)'
                    : pulse?.liquidity_state === 'CONTRACTION'
                    ? 'rgba(245, 158, 11, 0.16)'
                    : 'rgba(100, 116, 139, 0.16)',
                color:
                  pulse?.liquidity_state === 'EXPANSION'
                    ? '#06b6d4'
                    : pulse?.liquidity_state === 'CONTRACTION'
                    ? '#f59e0b'
                    : 'var(--text-muted)',
              }}
            >
              {pulse?.liquidity_state === 'EXPANSION' && '💧 充裕扩张 (EXPANSION)'}
              {pulse?.liquidity_state === 'CONTRACTION' && '🔥 紧缩承压 (CONTRACTION)'}
              {pulse?.liquidity_state === 'NEUTRAL' && '🌀 中性平稳 (NEUTRAL)'}
              {!pulse?.liquidity_state && '--'}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '10px' }}>
            美元指数 DXY 汇率与美债 10Y 基准利率联立驱动
          </div>
        </div>

        {/* Commodity Inflation Card */}
        <div
          style={{
            padding: '16px',
            borderRadius: '8px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            商品通胀压力指数 (Inflation State)
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '2px' }}>
            <span
              style={{
                padding: '3px 10px',
                borderRadius: '4px',
                background:
                  pulse?.inflation_state === 'HOT'
                    ? 'rgba(239, 68, 68, 0.16)'
                    : pulse?.inflation_state === 'COOLING'
                    ? 'rgba(59, 130, 246, 0.16)'
                    : 'rgba(16, 185, 129, 0.16)',
                color:
                  pulse?.inflation_state === 'HOT'
                    ? '#ef4444'
                    : pulse?.inflation_state === 'COOLING'
                    ? '#3b82f6'
                    : '#10b981',
              }}
            >
              {pulse?.inflation_state === 'HOT' && '🌋 高温过热 (HOT)'}
              {pulse?.inflation_state === 'COOLING' && '❄️ 降温回落 (COOLING)'}
              {pulse?.inflation_state === 'STABLE' && '🌱 温和稳定 (STABLE)'}
              {!pulse?.inflation_state && '--'}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '10px' }}>
            布伦特原油 (50%) + LME 铜 (50%) 工业原料加权
          </div>
        </div>

        {/* FTSE A50 Sentiment Card */}
        <div
          style={{
            padding: '16px',
            borderRadius: '8px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            富时中国 A50 隔夜离岸情绪
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '2px' }}>
            <span
              style={{
                padding: '3px 10px',
                borderRadius: '4px',
                background:
                  pulse?.ftse_a50_sentiment === 'BULLISH'
                    ? 'rgba(16, 185, 129, 0.16)'
                    : pulse?.ftse_a50_sentiment === 'BEARISH'
                    ? 'rgba(239, 68, 68, 0.16)'
                    : 'rgba(100, 116, 139, 0.16)',
                color:
                  pulse?.ftse_a50_sentiment === 'BULLISH'
                    ? '#10b981'
                    : pulse?.ftse_a50_sentiment === 'BEARISH'
                    ? '#ef4444'
                    : 'var(--text-muted)',
              }}
            >
              {pulse?.ftse_a50_sentiment === 'BULLISH' && '📈 离岸看涨 (BULLISH)'}
              {pulse?.ftse_a50_sentiment === 'BEARISH' && '📉 离岸看跌 (BEARISH)'}
              {pulse?.ftse_a50_sentiment === 'FLAT' && '➖ 波动中性 (FLAT)'}
              {!pulse?.ftse_a50_sentiment && '--'}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '10px' }}>
            新浪海外期货主力合约 (hf_CHA50CFD) 实时追踪
          </div>
        </div>
      </div>

      {/* 8 Authoritative Canonical Reference Anchors Grid */}
      <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '14px' }}>
        🏛️ 8 大核心宏观基准参考锚点 (Authoritative SSOT Universe)
      </h2>

      {anchors.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
          {errorMsg || '正在加载宏观参考锚点数据...'}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
            gap: '16px',
          }}
        >
          {anchors.map((item) => {
            const pctVal = Number(item.change_percent || 0);
            const isPositive = pctVal > 0;
            const sign = isPositive ? '+' : '';
            const roleDesc = ANCHOR_ROLES[item.symbol] || '全球宏观参考资产';

            return (
              <div
                key={item.symbol}
                style={{
                  padding: '18px',
                  borderRadius: '10px',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {item.display_name}
                    </span>
                    <span
                      style={{
                        fontSize: '0.68rem',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-subtle)',
                        background: 'rgba(255,255,255,0.03)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {item.provider_source}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px', fontFamily: 'monospace' }}>
                    {item.symbol} · {item.unit}
                  </div>

                  {/* Price & Change Display */}
                  <div style={{ marginTop: '14px', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'monospace' }}>
                      {item.last_price}
                    </span>
                    <span
                      style={{
                        fontSize: '0.88rem',
                        fontWeight: 600,
                        color: getChangeColor(item.change_percent),
                        fontFamily: 'monospace',
                      }}
                    >
                      {sign}{item.change_percent}%
                    </span>
                  </div>
                </div>

                {/* Tactical Role Annotation */}
                <div
                  style={{
                    marginTop: '16px',
                    paddingTop: '10px',
                    borderTop: '1px dashed var(--border-subtle)',
                    fontSize: '0.74rem',
                    color: 'var(--text-muted)',
                    lineHeight: '1.35',
                  }}
                >
                  📌 {roleDesc}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
